import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";

import { useAuth } from "@/lib/auth-context";
import { useVaultMode } from "@/hooks/use-vault-mode";

/**
 * Authenticated chrome for the vault app. Replaces the advisor app's
 * AppViewContainer + AppHeaderContainer, which are coupled to org/subscription
 * state that the vault population does not have. This is a lean header + nav.
 */

const NAV = [
  { to: "/", label: "Home", exact: true, ownerOnly: false },
  { to: "/files", label: "Documents", exact: false, ownerOnly: true },
  {
    to: "/trustees",
    label: "Trusted people",
    exact: false,
    ownerOnly: true,
  },
  { to: "/settings", label: "Settings", exact: false, ownerOnly: false },
] as const;

function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { effectiveMode, hasBoth, setMode } = useVaultMode();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const handleSwitchRole = () => {
    setMode(null);
    void router.navigate({ to: "/" });
  };

  // Owner-only items (files, trustee distribution) appear only while the user is
  // acting as an owner. A heir — or a dual-role user in trustee mode — never sees
  // them; backend authz (ClientAuth / vault.vault_memberships) remains the real gate.
  const showOwnerNav = effectiveMode === "owner";
  const nav = NAV.filter((item) => !item.ownerOnly || showOwnerNav);

  return (
    <div className="bg-background min-h-screen">
      <header className="bg-card sticky top-0 z-40 border-b">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-6 py-3">
          <div className="flex items-center gap-8">
            <span className="font-display font-semibold text-[var(--ds-color-secondary-500)]">
              Secure Legacy · Vault
            </span>
            <nav className="flex items-center gap-1">
              {nav.map((item) => {
                const active = item.exact
                  ? pathname === item.to
                  : pathname.startsWith(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={
                      "rounded-md px-3 py-1.5 text-sm font-medium no-underline transition-colors " +
                      (active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground")
                    }
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {hasBoth && (
              <button
                onClick={handleSwitchRole}
                className="text-muted-foreground hover:text-foreground text-xs font-medium"
                title="Switch between your owner and trustee roles"
              >
                Switch role
              </button>
            )}
            <span className="text-muted-foreground hidden text-sm sm:inline">
              {user?.email}
            </span>
            <button
              onClick={() => void logout()}
              className="ds-btn ds-btn-outline"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-8">{children}</main>
    </div>
  );
}

/**
 * Component-level auth gate (the vault app uses component gating rather than
 * TanStack `beforeLoad` guards — the advisor guards read org/platform claims
 * that don't exist here). Unauthenticated users are bounced to the index route,
 * which renders the sign-in screen.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (!loading && !user && pathname !== "/") {
      void router.navigate({ to: "/" });
    }
  }, [loading, user, pathname, router]);

  if (loading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }
  if (!user) return null;
  return <AppShell>{children}</AppShell>;
}

/**
 * Capability gate. Renders `children` only if the user holds the required vault
 * capability (owns a vault / is a heir); otherwise bounces to the home switcher.
 * Wrap owner-only routes with `need="owner"` and heir-only routes with
 * `need="heir"` so a direct URL can't cross the role boundary. Must sit inside
 * `RequireAuth` (it assumes the token/roles are loaded).
 */
export function RequireCapability({
  need,
  children,
}: {
  need: "owner" | "heir";
  children: ReactNode;
}) {
  const { loading, isOwner, isHeir } = useVaultMode();
  const router = useRouter();
  const has = need === "owner" ? isOwner : isHeir;

  useEffect(() => {
    if (!loading && !has) void router.navigate({ to: "/" });
  }, [loading, has, router]);

  if (loading || !has) return null;
  return <>{children}</>;
}
